import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Loader2, TrendingUp, Target, Zap } from "lucide-react";

const SPORT_LABELS = {
  mlb: "MLB",
  nba: "NBA",
  nhl: "NHL",
  mma: "UFC/MMA",
};

const TONE_CLASSES = {
  green: "neon-text",
  amber: "amber-text",
  red: "text-red-400",
};
const toneClass = (tone) => TONE_CLASSES[tone] || "text-white";

const RESULT_BADGE = {
  win: "border-[#00D4FF] text-[#00D4FF]",
  loss: "border-red-400 text-red-400",
  push: "border-zinc-400 text-zinc-400",
};
const resultBadgeClass = (result) =>
  RESULT_BADGE[result] || "border-zinc-700 text-zinc-500";

const hitRateTone = (pct) => (pct >= 52.38 ? "green" : "amber");

export default function TrackRecord() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/track-record");
      setData(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="track-record-page">
      <div className="border-b border-zinc-800 pb-10 mb-10 flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="overline mb-3">AI·RECEIPTS</div>
          <h1 className="font-display text-4xl lg:text-5xl">The AI track record.</h1>
          <p className="mt-4 text-zinc-400 max-w-2xl">
            Every AI pick we've published across MLB, NBA, NHL, NFL and UFC. Auto-graded
            against ESPN final scores the moment each game ends.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="track-refresh"
          className="flex items-center gap-2 px-4 py-2 border border-zinc-800 text-zinc-400 hover:border-[#00D4FF] hover:text-[#00D4FF] font-mono text-xs uppercase tracking-widest"
        >
          {loading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <TrendingUp size={14} strokeWidth={1.5} />
          )}
          Refresh
        </button>
      </div>

      {loading && !data ? (
        <div className="flex items-center gap-2 text-zinc-400 mono text-sm">
          <Loader2 size={16} className="animate-spin text-[#00D4FF]" /> Grading against
          ESPN…
        </div>
      ) : !data ? null : (
        <>
          {/* Overall stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-zinc-800 mb-10">
            {[
              { label: "TOTAL PICKS", val: data.overall.total, test: "tr-total" },
              { label: "GRADED", val: data.overall.graded, test: "tr-graded" },
              {
                label: "HITS",
                val: data.overall.wins,
                tone: "green",
                test: "tr-wins",
              },
              {
                label: "MISSES",
                val: data.overall.losses,
                tone: "red",
                test: "tr-losses",
              },
              {
                label: "HIT RATE",
                val: `${data.overall.hit_rate_pct}%`,
                tone: hitRateTone(data.overall.hit_rate_pct),
                test: "tr-hitrate",
              },
            ].map((s) => (
              <div
                key={s.label}
                data-testid={s.test}
                className="p-5 border-r border-zinc-800 last:border-r-0"
              >
                <div className="overline">{s.label}</div>
                <div className={`font-display text-3xl mt-2 ${toneClass(s.tone)}`}>
                  {s.val}
                </div>
              </div>
            ))}
          </div>

          {/* Per sport breakdown */}
          <div className="mb-10">
            <div className="overline mb-4">BY SPORT</div>
            <div className="grid md:grid-cols-4 gap-0 border border-zinc-800">
              {Object.entries(data.by_sport || {}).map(([sport, s]) => (
                <div
                  key={sport}
                  className="p-5 border-r border-zinc-800 last:border-r-0"
                  data-testid={`tr-sport-${sport}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="overline neon-text">{SPORT_LABELS[sport] || sport}</span>
                    <Target size={12} strokeWidth={1.5} className="text-zinc-600" />
                  </div>
                  <div className="font-display text-4xl mono text-white">
                    {s.hit_rate_pct}%
                  </div>
                  <div className="mono text-xs text-zinc-500 mt-1">
                    {s.wins}-{s.losses} ({s.graded}/{s.total} graded)
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pick log */}
          <div>
            <div className="overline mb-4">PICK LOG</div>
            {(data.picks || []).length === 0 ? (
              <div className="border border-dashed border-zinc-800 p-12 text-center">
                <div className="font-display text-xl text-zinc-400">No picks yet</div>
                <p className="text-sm text-zinc-500 mt-2">
                  As the AI publishes plays and games finish, they'll appear here.
                </p>
              </div>
            ) : (
              <div className="border border-zinc-800 overflow-x-auto">
                <table className="w-full mono text-sm min-w-[900px]" data-testid="tr-table">
                  <thead className="bg-zinc-900">
                    <tr className="text-left">
                      <th className="px-4 py-3 overline">DATE</th>
                      <th className="px-4 py-3 overline">SPORT</th>
                      <th className="px-4 py-3 overline">MATCHUP</th>
                      <th className="px-4 py-3 overline">PICK</th>
                      <th className="px-4 py-3 overline text-center">CONF</th>
                      <th className="px-4 py-3 overline">FINAL</th>
                      <th className="px-4 py-3 overline">RESULT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.picks.map((p) => (
                      <tr
                        key={p.game_id}
                        className="border-t border-zinc-800"
                        data-testid={`tr-row-${p.game_id}`}
                      >
                        <td className="px-4 py-3 text-zinc-500">
                          {p.start_time
                            ? new Date(p.start_time).toLocaleDateString(undefined, {
                                month: "short",
                                day: "numeric",
                              })
                            : "—"}
                        </td>
                        <td className="px-4 py-3 uppercase text-zinc-300">
                          {SPORT_LABELS[p.sport] || p.sport}
                        </td>
                        <td className="px-4 py-3 text-zinc-200">{p.matchup}</td>
                        <td className="px-4 py-3 neon-text">{p.pick}</td>
                        <td className="px-4 py-3 text-center amber-text">
                          {p.confidence}★
                        </td>
                        <td className="px-4 py-3 text-zinc-500 text-xs">
                          {p.final_score || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 uppercase text-[10px] tracking-wider border ${resultBadgeClass(
                              p.result
                            )}`}
                          >
                            {p.status === "graded" ? p.result : p.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-8 border border-zinc-800 bg-zinc-950/50 p-5 flex items-start gap-3">
            <Zap size={18} strokeWidth={1.5} className="text-[#00D4FF] mt-0.5 shrink-0" />
            <p className="text-xs text-zinc-400 leading-relaxed">
              Track record starts empty and fills as games finish. Picks are graded
              automatically using ESPN final scores. A {">"}52.38% hit rate at -110 is
              break-even; anything above is profitable at flat stakes.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
